/**
 * @module agent/adapters/services/BackendSyncService
 *
 * Handles backend data transformation and session synchronization.
 * Extracted from AgentCoreAdapter to reduce adapter complexity.
 *
 * Responsibilities:
 * - Transform backend session format to adapter format
 * - Transform backend message format to adapter format
 * - Merge backend sessions with locally-created sessions
 * - Identify orphaned message session IDs
 *
 * All methods are pure functions (no side effects, no external dependencies).
 */

import { MessagePhase } from '../../utils/MessagePhaseManager';

/**
 * Handles backend data transformation and sync operations.
 * All methods are pure functions - stateless service.
 */
class BackendSyncService {
  /**
   * Fix #6: Parse date with fallback to current time for invalid values.
   *
   * @param {string} dateString - ISO 8601 date string
   * @returns {Date} Parsed date or current date if invalid
   * @private
   */
  _parseDate(dateString) {
    const parsed = new Date(dateString);
    // Check for Invalid Date
    if (Number.isNaN(parsed.getTime())) {
      return new Date();
    }
    return parsed;
  }

  /**
   * Transform a backend session to adapter format.
   *
   * @param {Object} session - Backend session object
   * @param {string} session.session_id - Session ID
   * @param {string} [session.name] - Session name
   * @param {string} session.created_at - Creation timestamp (ISO 8601)
   * @param {string} session.updated_at - Last update timestamp (ISO 8601)
   * @param {boolean} [session.is_favorite] - Favorite flag
   * @param {boolean} [session.is_archived] - Archived flag
   * @returns {Object} Adapter-format session
   */
  transformBackendSession(session) {
    return {
      id: session.session_id,
      runtimeSessionId: session.session_id,
      title: session.name || 'Untitled Chat',
      // Fix #6: Use _parseDate to handle invalid timestamps gracefully
      createdAt: this._parseDate(session.created_at),
      lastActivity: this._parseDate(session.updated_at),
      isFavorite: session.is_favorite || false,
      isArchived: session.is_archived || false,
      isActive: false
    };
  }

  /**
   * Transform an array of backend messages to adapter format.
   * Handles Strands message structure with content blocks.
   *
   * Fix #4: Returns both messages and errors for caller visibility.
   *
   * @param {Array} backendMessages - Array of backend message objects
   * @returns {Object} { messages: Array, errors: Array } - Transformed messages and any errors
   */
  transformBackendMessages(backendMessages) {
    const messages = [];
    const errors = [];

    for (const backendMsg of backendMessages) {
      try {
        const { message } = backendMsg;

        // Validate required fields
        if (!message?.role || !message?.content) {
          continue;
        }

        // Extract text content from content blocks
        let textContent = '';
        if (Array.isArray(message.content)) {
          for (const block of message.content) {
            if (block.text) {
              textContent += block.text;
            }
          }
        } else if (typeof message.content === 'string') {
          // Fallback for legacy string format
          textContent = message.content;
        }

        // Skip messages with no text content
        if (!textContent) {
          continue;
        }

        // Create adapter-format message
        // Map backend role 'assistant' to adapter type 'ai' for rendering compatibility
        const adapterMessage = {
          // Fix #7: Use backend message_id if available to prevent ID conflicts
          id: backendMsg.message_id || crypto.randomUUID(),
          type: message.role === 'assistant' ? 'ai' : message.role,
          content: textContent,
          timestamp: backendMsg.created_at ? new Date(backendMsg.created_at) : new Date(),
          phase: MessagePhase.COMPLETE,
          isRestored: true
        };

        messages.push(adapterMessage);
      } catch (error) {
        // Fix #4: Collect errors for caller visibility
        errors.push({ message: backendMsg, error: error.message });
        console.error('❌ BackendSyncService: Error transforming message:', error);
      }
    }

    return { messages, errors };
  }

  /**
   * Merge backend sessions with locally-created sessions.
   * Preserves sessions that are _justCreated or _awaitingFirstMessage.
   *
   * @param {Array} backendSessions - Array of backend session objects
   * @param {Map} localSessions - Map of local sessions
   * @returns {Map} Merged session map
   */
  mergeSessionsWithLocal(backendSessions, localSessions) {
    // Identify locally-created sessions to preserve
    const locallyCreated = new Map();
    for (const [id, session] of localSessions) {
      if (session._justCreated || session._awaitingFirstMessage) {
        locallyCreated.set(id, session);
      }
    }

    // Start with transformed backend sessions
    const mergedSessions = new Map();
    for (const backendSession of backendSessions) {
      const transformed = this.transformBackendSession(backendSession);
      mergedSessions.set(transformed.id, transformed);
    }

    // Overlay locally-created sessions
    for (const [id, session] of locallyCreated) {
      mergedSessions.set(id, session);
    }

    return mergedSessions;
  }

  /**
   * Find session IDs that have messages but no corresponding session.
   *
   * @param {Set} validSessionIds - Set of valid session IDs
   * @param {Array} messageSessionIds - Array of session IDs with messages
   * @returns {Array} Array of orphaned session IDs
   */
  findOrphanedMessageSessionIds(validSessionIds, messageSessionIds) {
    return messageSessionIds.filter((id) => !validSessionIds.has(id));
  }
}

export default BackendSyncService;
